<script setup>
import { onMounted, onUnmounted, ref } from "vue"

const props = defineProps({
  statusUrl: { type: String, required: true },
  loginUrl: { type: String, required: true },
  logoutUrl: { type: String, required: true },
})

const status = ref(null)
const loading = ref(true)
const action = ref("")
const error = ref("")
let pollTimer = null

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

async function request(url, options = {}) {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
  })
  const payload = await response.json()
  if (!response.ok) throw new Error(payload?.error?.message || `請求失敗（${response.status}）`)
  return payload
}

async function loadStatus() {
  try {
    status.value = await request(props.statusUrl)
    error.value = status.value.error || ""
    schedulePollIfNeeded()
  } catch (reason) {
    error.value = reason.message || "無法讀取 Codex 登入狀態"
  } finally {
    loading.value = false
  }
}

function schedulePollIfNeeded() {
  clearTimeout(pollTimer)
  if (["starting", "pending"].includes(status.value?.login?.status)) {
    pollTimer = window.setTimeout(loadStatus, 1800)
  }
}

async function startLogin() {
  action.value = "login"
  error.value = ""
  try {
    status.value = {
      ...(status.value || {}),
      login: await request(props.loginUrl, { method: "POST" }),
    }
    schedulePollIfNeeded()
  } catch (reason) {
    error.value = reason.message || "無法啟動 ChatGPT 裝置登入"
  } finally {
    action.value = ""
  }
}

async function logout() {
  if (!window.confirm("登出後，IEP 與會議的 AI 逐字稿助手會停止運作。確定登出？")) return
  action.value = "logout"
  error.value = ""
  try {
    status.value = await request(props.logoutUrl, { method: "DELETE" })
  } catch (reason) {
    error.value = reason.message || "Codex 登出失敗"
  } finally {
    action.value = ""
  }
}

async function copyCode() {
  await navigator.clipboard.writeText(status.value?.login?.user_code || "")
}

onMounted(loadStatus)
onUnmounted(() => clearTimeout(pollTimer))
</script>

<template>
  <div class="ai-provider-card">
    <div v-if="loading" class="ai-provider-loading">正在確認 Codex 帳號狀態…</div>
    <p v-if="error" class="ai-review-error" role="alert">{{ error }}</p>

    <template v-if="!loading && status">
      <div v-if="status.login?.status === 'connected'" class="ai-provider-connected">
        <span class="ai-provider-signal" aria-hidden="true"></span>
        <div><small>目前連結</small><strong>{{ status.login.email || "ChatGPT 帳號" }}</strong><p>{{ status.login.plan_type || "ChatGPT" }} workspace</p></div>
        <button type="button" :disabled="action === 'logout'" @click="logout">{{ action === "logout" ? "登出中…" : "登出" }}</button>
      </div>

      <div v-else-if="['starting', 'pending'].includes(status.login?.status)" class="ai-provider-pending">
        <p><span>1</span>開啟 OpenAI 官方裝置登入頁面</p>
        <a v-if="status.login.verification_url" :href="status.login.verification_url" target="_blank" rel="noopener">開啟登入頁面 ↗</a>
        <p><span>2</span>輸入一次性代碼</p>
        <button v-if="status.login.user_code" type="button" class="ai-device-code" @click="copyCode">{{ status.login.user_code }} <small>點擊複製</small></button>
        <div class="ai-provider-waiting"><i aria-hidden="true"></i>等待 OpenAI 完成驗證…</div>
      </div>

      <div v-else class="ai-provider-disconnected">
        <span aria-hidden="true">✦</span>
        <div><h2>尚未連結 ChatGPT</h2><p>系統不會要求上傳 auth.json，也不會把 OAuth token 傳到瀏覽器。</p></div>
        <button type="button" :disabled="action === 'login'" @click="startLogin">{{ action === "login" ? "啟動中…" : "連結 ChatGPT 訂閱" }}</button>
      </div>

      <p v-if="!status.enabled" class="ai-provider-flag-note">正式環境功能旗標目前關閉；完成測試後設定 <code>AI_ASSISTANT_ENABLED=true</code> 才會顯示表單入口。</p>
    </template>

    <footer>
      <strong>隔離原則</strong>
      <span>每次任務使用獨立臨時目錄，禁止工具、網路、MCP、Plugins 與多代理；逐字稿不寫入應用程式日誌。</span>
    </footer>
  </div>
</template>
